import api from './client';

export type StripeMode = 'test' | 'live';
export type StripeKeyName = 'publishableKey' | 'secretKey' | 'webhookSecret';

/**
 * Saved keys for one mode, '' where nothing is saved. Publishable keys arrive
 * in full; secrets arrive masked (sk_test_••••••••Ab12).
 */
export type StripeModeKeys = Record<StripeKeyName, string>;

export interface StripeSettings {
  mode: StripeMode;
  updatedAt: string | null;
  test: StripeModeKeys;
  live: StripeModeKeys;
}

/**
 * PUT payload. Leave a field out to keep it, send a string to set it, or
 * '' / null to remove it.
 */
export interface StripeSettingsUpdate {
  mode?: StripeMode;
  test?: Partial<Record<StripeKeyName, string | null>>;
  live?: Partial<Record<StripeKeyName, string | null>>;
}

export interface StripePublicConfig {
  mode: StripeMode;
  publishableKey: string;
}

interface StripeSettingsResponse {
  status: number;
  message?: string;
  stripe: StripeSettings;
}

/** GET /api/settings/stripe (admin) */
export const getStripeSettings = async (): Promise<StripeSettings> => {
  const res = await api.get<StripeSettingsResponse>('/settings/stripe');
  return res.data.stripe;
};

/** PUT /api/settings/stripe (admin) */
export const updateStripeSettings = async (
  payload: StripeSettingsUpdate
): Promise<StripeSettings> => {
  const res = await api.put<StripeSettingsResponse>('/settings/stripe', payload);
  return res.data.stripe;
};

/** GET /api/stripe/config (public) — publishable key for the active mode. */
export const getStripePublicConfig = async (): Promise<StripePublicConfig> => {
  const res = await api.get<StripePublicConfig & { status: number }>('/stripe/config');
  return { mode: res.data.mode, publishableKey: res.data.publishableKey };
};

// ─── Webhook endpoint ──────────────────────────────────────────────

export interface StripeWebhookEndpoint {
  id: string;
  url: string;
  status: 'enabled' | 'disabled';
  apiVersion: string | null;
  created: string | null;
  /** Points at this server's address. */
  isCurrent: boolean;
  /** Created from the admin console, so the saved signing secret is its secret. */
  secretSaved: boolean;
  missingEvents: string[];
}

export interface StripeWebhookStatus {
  mode: StripeMode;
  /** The address Stripe should call. */
  url: string;
  /** Events the app handles. */
  events: string[];
  hasSecretKey: boolean;
  hasSigningSecret: boolean;
  /** This app's endpoints in the Stripe account, current address first. */
  endpoints: StripeWebhookEndpoint[];
}

interface WebhookResponse {
  status: number;
  message?: string;
  webhook: StripeWebhookStatus;
  stripe?: StripeSettings;
}

/** GET /api/settings/stripe/webhook?mode= (admin) */
export const getStripeWebhook = async (mode: StripeMode): Promise<StripeWebhookStatus> => {
  const res = await api.get<WebhookResponse>('/settings/stripe/webhook', { params: { mode } });
  return res.data.webhook;
};

/**
 * POST /api/settings/stripe/webhook (admin) — creates or replaces the
 * endpoint and saves its signing secret. Talks to Stripe, so allow longer.
 */
export const setupStripeWebhook = async (
  mode: StripeMode
): Promise<{ message: string; webhook: StripeWebhookStatus; stripe: StripeSettings }> => {
  const res = await api.post<WebhookResponse>('/settings/stripe/webhook', { mode }, { timeout: 45000 });
  return {
    message: res.data.message || 'Webhook created',
    webhook: res.data.webhook,
    stripe: res.data.stripe as StripeSettings,
  };
};

/** DELETE /api/settings/stripe/webhook/:id?mode= (admin) */
export const removeStripeWebhook = async (
  mode: StripeMode,
  endpointId: string
): Promise<StripeWebhookStatus> => {
  const res = await api.delete<WebhookResponse>(
    `/settings/stripe/webhook/${encodeURIComponent(endpointId)}`,
    { params: { mode }, timeout: 45000 }
  );
  return res.data.webhook;
};
